/*
 * image.js — the track as a picture (PNG for a message or a post) and as a
 * printable report (PDF: map, numbers, profile, splits — for a hike you want on
 * paper).
 *
 *   GpsImage.render({ kind:'png'|'pdf', track, derived, units, t, tiles, panels,
 *                     title, brand, stats, splits, jspdfUrl }) → Promise<Blob>
 *
 * Everything is drawn on our own canvas: the basemap is the site's shared one
 * (js/map-tiles.js), rendered for exactly this frame by an off-screen MapLibre
 * and painted under the track. A screenshot of the live map cannot be used —
 * it has the page's size and view, not the report's. Text is drawn by the browser (any script, Arabic
 * and Korean included) and the PDF carries the page as an image, so no font
 * embedding is needed for Cyrillic or Hangul.
 */
(function () {
    'use strict';
    var TILE = 256, RAD = Math.PI / 180;
    function px(lat, lon, z) {
        var n = Math.pow(2, z) * TILE;
        var s = Math.sin(Math.max(-85.05, Math.min(85.05, lat)) * RAD);
        return [(lon + 180) / 360 * n, (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n];
    }

    function drawMap(ctx, X, Y, Wd, Ht, sr, color, tiles) {
        var la0 = 90, la1 = -90, lo0 = 180, lo1 = -180;
        for (var i = 0; i < sr.n; i++) { la0 = Math.min(la0, sr.lat[i]); la1 = Math.max(la1, sr.lat[i]); lo0 = Math.min(lo0, sr.lon[i]); lo1 = Math.max(lo1, sr.lon[i]); }
        var pad = 60, z = 17;
        for (; z > 1; z--) {
            var a = px(la1, lo0, z), b = px(la0, lo1, z);
            if (b[0] - a[0] <= Wd - pad * 2 && b[1] - a[1] <= Ht - pad * 2) break;
        }
        var A = px(la1, lo0, z), B = px(la0, lo1, z);
        var cx = (A[0] + B[0]) / 2, cy = (A[1] + B[1]) / 2;
        var left = cx - Wd / 2, top = cy - Ht / 2;
        ctx.save();
        ctx.beginPath(); ctx.rect(X, Y, Wd, Ht); ctx.clip();
        ctx.fillStyle = '#eef0f3'; ctx.fillRect(X, Y, Wd, Ht);
        var mapSource = 'vector';
        // rendered at 2x and drawn at 1x: labels and thin roads stay crisp in the file
        var basemap = window.__mapTiles && window.__mapTiles.snapshot
            ? window.__mapTiles.snapshot({ style: 'street', zoom: z, x: left, y: top, width: Wd, height: Ht, pixelRatio: 2 })
                .then(function (bm) { if (bm) { ctx.drawImage(bm, X, Y, Wd, Ht); mapSource = bm.mapSource || mapSource; } }, function () {})
            : Promise.resolve();
        return basemap.then(function () {
            ctx.lineJoin = 'round'; ctx.lineCap = 'round';
            var P = function (k) { var p = px(sr.lat[k], sr.lon[k], z); return [X + p[0] - left, Y + p[1] - top]; };
            var starts = {};
            sr.segStart.forEach(function (s) { starts[s] = 1; });
            [['rgba(255,255,255,.95)', 9], [color, 5]].forEach(function (st) {
                ctx.strokeStyle = st[0]; ctx.lineWidth = st[1];
                ctx.beginPath();
                for (var k = 0; k < sr.n; k++) { var q = P(k); if (k === 0 || starts[k]) ctx.moveTo(q[0], q[1]); else ctx.lineTo(q[0], q[1]); }
                ctx.stroke();
            });
            var s = P(0), e = P(sr.n - 1);
            var loop = Math.abs(s[0] - e[0]) < 18 && Math.abs(s[1] - e[1]) < 18;
            // a loop starts and ends in one place: a green ring round the red
            // finish, instead of the finish painting over the start
            ctx.lineWidth = 3; ctx.strokeStyle = '#fff';
            if (loop) { ctx.beginPath(); ctx.arc(e[0], e[1], 14, 0, Math.PI * 2); ctx.fillStyle = '#1fb35a'; ctx.fill(); ctx.stroke(); }
            else { ctx.beginPath(); ctx.arc(s[0], s[1], 9, 0, Math.PI * 2); ctx.fillStyle = '#1fb35a'; ctx.fill(); ctx.stroke(); }
            ctx.beginPath(); ctx.arc(e[0], e[1], loop ? 8 : 9, 0, Math.PI * 2); ctx.fillStyle = '#e5484d'; ctx.fill(); ctx.stroke();
            ctx.restore();
            ctx.fillStyle = 'rgba(255,255,255,.85)';
            ctx.font = '12px system-ui,-apple-system,Segoe UI,sans-serif';
            var attr = window.__mapTiles && window.__mapTiles.creditText ? window.__mapTiles.creditText(mapSource) : '© OpenStreetMap contributors';
            var w = ctx.measureText(attr).width + 12;
            ctx.fillRect(X + Wd - w, Y + Ht - 20, w, 20);
            ctx.fillStyle = '#333'; ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
            ctx.fillText(attr, X + Wd - 6, Y + Ht - 10);
            ctx.textAlign = 'left';
        });
    }

    function drawProfile(ctx, X, Y, Wd, Ht, o) {
        var d = o.derived, sr = d.sr;
        var p = (o.panels || []).filter(function (q) { return q.key === 'ele'; })[0];
        if (!p || sr.n < 2) return;
        var v = p.v, mn = Infinity, mx = -Infinity;
        for (var i = 0; i < v.length; i++) { if (v[i] === v[i]) { if (v[i] < mn) mn = v[i]; if (v[i] > mx) mx = v[i]; } }
        if (!isFinite(mn)) return;
        if (mx - mn < 20) { mx += 10; mn -= 10; }
        var span = mx - mn; mn -= span * 0.08; mx += span * 0.08;
        var dist = sr.dist, D = dist[sr.n - 1] || 1, L = 58;
        ctx.fillStyle = '#6b7280'; ctx.font = '600 14px system-ui,-apple-system,Segoe UI,sans-serif';
        ctx.textBaseline = 'top'; ctx.textAlign = 'left';
        ctx.fillText(p.label, X, Y);
        var top = Y + 24, h = Ht - 50, w = Wd - L;
        ctx.strokeStyle = 'rgba(0,0,0,.08)'; ctx.lineWidth = 1;
        ctx.font = '12px system-ui,-apple-system,Segoe UI,sans-serif'; ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
        for (var k = 0; k <= 3; k++) {
            var yv = mn + (mx - mn) * k / 3, yy = top + h - h * k / 3;
            ctx.beginPath(); ctx.moveTo(X + L, yy); ctx.lineTo(X + Wd, yy); ctx.stroke();
            ctx.fillStyle = '#6b7280'; ctx.fillText(p.fmt ? p.fmt(yv) : Math.round(yv), X + L - 6, yy);
        }
        var g = ctx.createLinearGradient(0, top, 0, top + h);
        g.addColorStop(0, 'rgba(47,179,122,.5)'); g.addColorStop(1, 'rgba(47,179,122,.06)');
        ctx.beginPath(); ctx.moveTo(X + L, top + h);
        for (var j = 0; j < sr.n; j++) { if (v[j] !== v[j]) continue; ctx.lineTo(X + L + dist[j] / D * w, top + h - (v[j] - mn) / (mx - mn) * h); }
        ctx.lineTo(X + L + w, top + h); ctx.closePath(); ctx.fillStyle = g; ctx.fill();
        ctx.beginPath();
        var st = false;
        for (j = 0; j < sr.n; j++) { if (v[j] !== v[j]) { st = false; continue; } var xx = X + L + dist[j] / D * w, y2 = top + h - (v[j] - mn) / (mx - mn) * h; if (!st) { ctx.moveTo(xx, y2); st = true; } else ctx.lineTo(xx, y2); }
        ctx.strokeStyle = '#2fb37a'; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = '#6b7280'; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
        var unit = o.units.unitLen(), totalU = D / unit, step = [0.5, 1, 2, 5, 10, 20, 50, 100, 200].filter(function (s) { return totalU / s <= 10; })[0] || 500;
        for (var u = 0; u <= totalU + 1e-9; u += step) ctx.fillText((Math.round(u * 10) / 10) + '', X + L + u * unit / D * w, top + h + 6);
    }

    function drawStats(ctx, X, Y, Wd, stats, cols) {
        cols = cols || 4;
        var cw = Wd / cols;
        stats.forEach(function (s, i) {
            var cx = X + (i % cols) * cw, cy = Y + Math.floor(i / cols) * 74;
            ctx.textAlign = 'left'; ctx.textBaseline = 'top';
            ctx.fillStyle = '#6b7280'; ctx.font = '600 15px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.fillText(s[0], cx, cy);
            ctx.fillStyle = '#111827'; ctx.font = '800 28px system-ui,-apple-system,Segoe UI,sans-serif';
            fitText(ctx, s[1], cx, cy + 22, cw - 16);
        });
        return Math.ceil(stats.length / cols) * 74;
    }
    function fitText(ctx, s, x, y, max) {
        var size = 28;
        while (size > 14 && ctx.measureText(s).width > max) { size -= 2; ctx.font = '800 ' + size + 'px system-ui,-apple-system,Segoe UI,sans-serif'; }
        ctx.fillText(s, x, y);
    }

    function header(ctx, W, o) {
        ctx.fillStyle = '#111827';
        ctx.font = '800 38px system-ui,-apple-system,Segoe UI,sans-serif';
        ctx.textAlign = 'left'; ctx.textBaseline = 'top';
        var title = o.title || '';
        while (title.length > 4 && ctx.measureText(title).width > W - 120) title = title.slice(0, -2);
        if (title !== (o.title || '')) title += '…';
        ctx.fillText(title, 50, 40);
    }
    function footer(ctx, W, H, brand) {
        ctx.fillStyle = '#9ca3af'; ctx.font = '600 14px system-ui,-apple-system,Segoe UI,sans-serif';
        ctx.textAlign = 'right'; ctx.textBaseline = 'bottom';
        ctx.fillText(brand, W - 50, H - 24);
    }

    function png(o) {
        var W = 1600, H = 1300;
        var c = document.createElement('canvas'); c.width = W; c.height = H;
        var ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
        header(ctx, W, o);
        return drawMap(ctx, 50, 100, W - 100, 760, o.derived.sr, o.track.color || '#e5484d', o.tiles).then(function () {
            var h = drawStats(ctx, 50, 890, W - 100, o.stats.slice(0, 8), 4);
            drawProfile(ctx, 50, 890 + h + 10, W - 100, H - (890 + h + 10) - 60, o);
            footer(ctx, W, H, o.brand);
            return new Promise(function (res, rej) { c.toBlob(function (b) { b ? res(b) : rej(new Error('toBlob')); }, 'image/png'); });
        });
    }

    function loadJsPdf(url) {
        if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
        return new Promise(function (res, rej) {
            var s = document.createElement('script');
            s.src = url; s.onload = function () { window.jspdf && window.jspdf.jsPDF ? res(window.jspdf.jsPDF) : rej(new Error('jspdf')); };
            s.onerror = function () { rej(new Error('jspdf')); };
            document.head.appendChild(s);
        });
    }

    function pdf(o) {
        // page 1: header, map, numbers, profile — A4 at 150 dpi is 1240×1754
        var W = 1240, H = 1754;
        var c = document.createElement('canvas'); c.width = W; c.height = H;
        var ctx = c.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H);
        header(ctx, W, o);
        return drawMap(ctx, 50, 100, W - 100, 820, o.derived.sr, o.track.color || '#e5484d', o.tiles).then(function () {
            var h = drawStats(ctx, 50, 950, W - 100, o.stats, 3);
            drawProfile(ctx, 50, 950 + h + 16, W - 100, 330, o);
            var pages = [c];
            var y = 950 + h + 16 + 350;
            var sp = o.splits || [];
            if (sp.length) {
                var cur = c, cctx = ctx;
                var rowH = 30;
                var drawHead = function (yy) {
                    cctx.fillStyle = '#6b7280'; cctx.font = '700 15px system-ui,-apple-system,Segoe UI,sans-serif';
                    cctx.textAlign = 'left'; cctx.textBaseline = 'top';
                    var cols = [(o.units.unitLen() > 1500 ? o.t('u_mi') : o.t('u_km')), o.t('col_time'), o.foot ? o.t('p_pace') : o.t('p_speed'), '↑ / ↓', o.hasHr ? o.t('p_hr') : ''];
                    [50, 170, 380, 620, 860].forEach(function (x, i) { cctx.fillText(cols[i], x, yy); });
                    return yy + rowH;
                };
                if (y > H - 200) { cur = newPage(); cctx = cur.getContext('2d'); pages.push(cur); y = 60; }
                y = drawHead(y);
                sp.forEach(function (s, i) {
                    if (y > H - 80) { cur = newPage(); cctx = cur.getContext('2d'); pages.push(cur); y = drawHead(60); }
                    var tt = s.moving != null ? s.moving : s.elapsed;
                    cctx.fillStyle = i % 2 ? '#ffffff' : '#f5f6f8'; cctx.fillRect(40, y - 4, W - 80, rowH);
                    cctx.fillStyle = '#111827'; cctx.font = '15px system-ui,-apple-system,Segoe UI,sans-serif';
                    cctx.fillText(String(i + 1), 50, y);
                    cctx.fillText(tt ? o.units.dur(tt) : '—', 170, y);
                    cctx.fillText(tt ? (o.foot ? o.units.pace(s.distance / tt) : o.units.speed(s.distance / tt)) : '—', 380, y);
                    cctx.fillText(s.gain != null ? '+' + Math.round(s.gain * o.units.eleK()) + ' / −' + Math.round(s.loss * o.units.eleK()) : '—', 620, y);
                    if (o.hasHr) cctx.fillText(s.hr ? String(Math.round(s.hr)) : '—', 860, y);
                    y += rowH;
                });
            }
            pages.forEach(function (pg, i) { footer(pg.getContext('2d'), W, H, o.brand + (pages.length > 1 ? '  ·  ' + (i + 1) + '/' + pages.length : '')); });
            return loadJsPdf(o.jspdfUrl).then(function (jsPDF) {
                var docp = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
                pages.forEach(function (pg, i) {
                    if (i) docp.addPage();
                    docp.addImage(pg.toDataURL('image/jpeg', 0.9), 'JPEG', 0, 0, 210, 297);
                });
                return docp.output('blob');
            });
            function newPage() { var p = document.createElement('canvas'); p.width = W; p.height = H; var x = p.getContext('2d'); x.fillStyle = '#fff'; x.fillRect(0, 0, W, H); return p; }
        });
    }

    window.GpsImage = { render: function (o) { return o.kind === 'pdf' ? pdf(o) : png(o); } };
})();
